<template>
  <div>
    <panel>
      <div class="row">
        <div v-if="!procedure.interpretation" class="card-body details-card col-6">
          <p>
            Procedura:
            <strong>{{ procedure.name }}</strong>
          </p>

          <p>
            Identyfikator:
            <strong>{{ procedure.id }}</strong>
          </p>
        </div>

        <div v-else class="card-body details-card col-6 interpretation">
          <div v-html="procedure.interpretation" />

          <p>
            <strong>Identyfikator:</strong>
            {{ procedure.id }}
          </p>
        </div>

        <div class="py-3 col-6">
          <div v-if="procedure.description" :class="{ 'procedure__description--collapsed': collapseDescription }">
            <div ref="description" v-html="procedure.description" />
          </div>

          <div v-if="procedure.description && showCollapseDescriptionButton" class="text-right pt-3">
            <w-button
              class="btn-light btn-sm mr-2"
              :text="collapseDescription ? 'Pokaż więcej' : 'Pokaż mniej'"
              @click="collapseDescription = !collapseDescription"
            />
          </div>

          <attachments />
        </div>
      </div>

      <div class="float-right">
        <system-message
          button-class="btn btn-light mr-2 "
          icon="fal fa-fw fa-bell"
          :entity-type="entityType"
          :entity-id="procedure.id.toString()"
          :name="messageName"
        />

        <post-form
          v-if="!renderOptions.disabled"
          button-class="mr-2 btn-light"
          icon="fal fa-cloud-download mr-2"
          text="Wzór wniosku"
          :url="renderUrl"
          :fields="renderParams"
        />

        <post-form
          v-if="renderViewOptions.viewId && !!renderViewOptions.text"
          button-class="mr-2 btn-light"
          icon="fal fa-cloud-download mr-2"
          :url="renderViewUrl"
          :text="renderViewOptions.text"
        />
      </div>

      <actions
        slot="footer"
        :actions="actions"
        :entity-type="entityType"
        :object="procedure"
        :force-not-exact="true"
        @delete="showConfirmModal({ id: $event.object.id })"
        @edit="goToEdit($event.object.id)"
        @execute="execute"
        @navigate="navigate"
        @setStatus="openStatusPicker($event.object)"
      />
    </panel>

    <pick-status-modal
      :id="statusPickerProcedureId"
      :show="showStatusPicker"
      :object="statusPickerProcedure"
      @update="saveStatus"
      @cancel="closeStatusPicker"
    />

    <confirm-modal
      :show="confirmModalVisible"
      :submit-data="submitData"
      :text="confirmModalText"
      @submit="removeProcedure($event.id)"
      @close="hideConfirmModal"
    />

    <schema-form-modal :object="actionData" :show="!!actionData" @save="sendModalData" @close="closeActionDataModal" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Actions from '@/components/actions/Actions';
import SystemMessage from '@/components/formComponents/SystemMessage';
import PostForm from '@/components/postForm/PostForm';
import Attachments from '@/components/procedure/Attachments';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { StageType } from '@/enums/stageType.enum';
import { Successes } from '@/enums/successes.enum';
import { TableName } from '@/enums/tableName';
import { buttonsMixin } from '@/mixins/buttons';
import { confirmModalMixin } from '@/mixins/confirmModal';
import { procedureManageMixin } from '@/mixins/procedure/procedureManage';
import { config } from '@/utils/config';
import { DELETE_ACTION, EDIT_ACTION, SET_STATUS_ACTION } from '@/utils/constants';
import { getProcedureViewData } from '@/utils/procedure';

// @vuese
// Widok procedury
// @group Procedure
export default {
  components: { Actions, Attachments, PostForm, SystemMessage },
  mixins: [
    procedureManageMixin,
    buttonsMixin(
      StageType.PROCEDURE,
      [SET_STATUS_ACTION, EDIT_ACTION, { ...DELETE_ACTION, class: 'float-right' }],
      getProcedureViewData
    ),
    confirmModalMixin
  ],
  data() {
    return {
      entityType: TableName.procedure,
      collapseDescription: true,
      maxHeight: 150,
      confirmModalText: 'Kliknij, aby potwierdzić usunięcie procedury'
    };
  },
  computed: {
    ...mapState({
      procedure: state => state.procedure.data,
      viewId: state => state.setting.settings[PublicSettings.DOCUMENT_VIEW_ID]?.value,
      renderOptions: state => JSON.parse(state.setting.settings[PublicSettings.RENDER_EMPTY_OPTIONS]?.value || '{}'),
      renderViewOptions: state => JSON.parse(state.setting.settings[PublicSettings.RENDER_VIEW_OPTIONS]?.value || '{}')
    }),
    messageName() {
      return `Przypomnienie o procedurze: "${this.procedure.name}"`;
    },
    showCollapseDescriptionButton() {
      const descriptionElement = this.$refs.description;
      if (!descriptionElement) {
        return;
      }
      return descriptionElement.clientHeight > this.maxHeight;
    },
    renderUrl() {
      return `${config.renderURL}/pdf/procedure/${this.procedure.id}/${this.viewId}`;
    },
    renderParams() {
      if (!this.renderOptions.body) {
        return [];
      }
      const params = this.renderOptions.body;
      return Object.keys(params).map(key => ({ name: key, value: params[key] }));
    },
    renderViewUrl() {
      return `${config.renderURL}/pdf/procedure/${this.procedure.id}/${this.renderViewOptions?.viewId}`;
    }
  },
  methods: {
    // @vuese
    // Usuwa Procedurę
    removeProcedure(id) {
      this.$store
        .dispatch('procedure/remove', id)
        .then(() => {
          this.notifySuccess(Successes.PROCEDURE_DELETED);
          this.$router.push({ name: 'procedureList' });
        })
        .catch(err => this.notifyError(Errors.PROCEDURE_CANNOT_DELETE));
    }
  },
  beforeRouteUpdate(to, from, next) {
    this.$store.commit('procedure/setId', to.params.procedureId);
    getProcedureViewData(to)
      .then(() => next())
      .catch(err => this.notifyError(Errors.PROCEDURE_CANNOT_GET));
  }
};
</script>

<style>
.procedure__description--collapsed {
  max-height: 150px;
  overflow: hidden;
}

.interpretation p {
  font-weight: normal;
}

.interpretation p > strong {
  font-weight: bold;
}
</style>
